import React from 'react';
import { isEmpty } from 'lodash';

import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import { ChevronLeftIcon } from '~/components/icons';
import cn from '~/Utils/cn';

import { MINI_DRAWER_DEFAULTS } from './constants';
import { MiniDrawerProvider } from './MiniDrawerContext';
import MiniDrawerCustom from './MiniDrawerCustom';
import MiniDrawerDivider from './MiniDrawerDivider';
import MiniDrawerItem from './MiniDrawerItem';
import MiniDrawerSection from './MiniDrawerSection';
import type { MiniDrawerProps } from './types';

export { useMiniDrawer } from './MiniDrawerContext';
export type { MiniDrawerItemConfig, MiniDrawerProps } from './types';

interface MiniDrawerComponent extends React.FC<MiniDrawerProps> {
  Item: typeof MiniDrawerItem;
  Section: typeof MiniDrawerSection;
  Custom: typeof MiniDrawerCustom;
  Divider: typeof MiniDrawerDivider;
}

const MiniDrawer: MiniDrawerComponent = ({
  open,
  onToggle,
  children,
  selectedKey,
  onItemSelect,
  expandedWidth = MINI_DRAWER_DEFAULTS.EXPANDED_WIDTH,
  collapsedWidth = MINI_DRAWER_DEFAULTS.COLLAPSED_WIDTH,
  headerContent,
  footerContent,
  className,
  headerClassName,
  itemsClassName,
  footerClassName,
  itemClassName,
  selectedItemClassName,
  dividerClassName,
  items,
  iconSize = MINI_DRAWER_DEFAULTS.ICON_SIZE,
  transitionDuration = MINI_DRAWER_DEFAULTS.TRANSITION_DURATION_MS,
  hideToggleButton = false,
  toggleButtonPosition = 'header',
  ToggleIcon = ChevronLeftIcon,
  toggleButtonClassName,
}) => {
  const handleToggle = () => {
    onToggle(!open);
  };

  const handleItemSelect = (key: string) => {
    onItemSelect?.(key);
  };

  const ToggleButton = () => {
    if (hideToggleButton) return null;

    return (
      <IconButton
        onClick={handleToggle}
        className={cn(
          'flex-shrink-0 rounded-lg p-8 transition-transform',
          { 'rotate-180': !open },
          toggleButtonClassName
        )}
        aria-label={open ? 'Collapse drawer' : 'Expand drawer'}
      >
        <ToggleIcon size={MINI_DRAWER_DEFAULTS.TOGGLE_ICON_SIZE} iconColor="currentColor" />
      </IconButton>
    );
  };

  const contextValue = {
    isExpanded: open,
    expandedWidth,
    collapsedWidth,
    transitionDuration,
    iconSize,
    selectedKey,
    onItemSelect: handleItemSelect,
    itemClassName,
    selectedItemClassName,
    dividerClassName,
  };

  // Render items from array (legacy API)
  const renderItems = () => {
    if (isEmpty(items)) return null;

    return items?.map((item) => (
      <React.Fragment key={item.key}>
        <MiniDrawerItem
          itemKey={item.key}
          Icon={item.Icon}
          label={item.label}
          onClick={item.onClick}
          disabled={item.disabled}
          to={item.to}
          href={item.href}
          className={item.className}
          selectedClassName={item.selectedClassName}
        />
        {item.dividerAfter && <MiniDrawerDivider />}
      </React.Fragment>
    ));
  };

  return (
    <MiniDrawerProvider value={contextValue}>
      <div
        className={cn('flex h-full flex-col transition-all ease-in-out', className)}
        style={{
          width: open ? expandedWidth : collapsedWidth,
          transitionDuration: `${transitionDuration}ms`,
        }}
      >
        {/* Header */}
        <div className={cn('flex flex-shrink-0 items-center gap-8 overflow-hidden px-16', headerClassName)}>
          {toggleButtonPosition === 'header' && <ToggleButton />}
          {open && headerContent}
        </div>

        {/* Navigation Items */}
        <nav className={cn('flex-1 overflow-y-auto overflow-x-hidden py-16', itemsClassName)} role="navigation">
          {items && items.length > 0 ? renderItems() : children}
        </nav>

        {/* Footer */}
        {(footerContent || toggleButtonPosition === 'footer') && (
          <div className={cn('flex items-center gap-8 overflow-hidden p-16', footerClassName)}>
            {toggleButtonPosition === 'footer' && <ToggleButton />}
            {footerContent}
          </div>
        )}
      </div>
    </MiniDrawerProvider>
  );
};

// Attach sub-components for compound component pattern
MiniDrawer.Item = MiniDrawerItem;
MiniDrawer.Section = MiniDrawerSection;
MiniDrawer.Custom = MiniDrawerCustom;
MiniDrawer.Divider = MiniDrawerDivider;

export default MiniDrawer;
